import type { ClientSummary } from '@/features/Clients/Data';
import type { CollaboratorPublic } from '@/features/Collaborators/Data';
import type { Solicitacao, Movimentacao } from '../../models';
import { textoCorresponde } from '../../utils/buscaTexto';
import { TIPO_SOLICITACAO_LABELS } from '../NovaSolicitacaoPage/novaSolicitacaoPage.definitions';
import { STATUS_SOLICITACAO_LABELS } from './solicitacoesListPage.definitions';
import { movimentacaoAtende, type SelecaoResumo } from './solicitacoesResumo.utils';
import { paraDataFormatada } from './components/solicitacoesKanban.utils';

/**
 * Como o Controle de Solicitações lista: uma linha por Movimentação (cada uma na sua data —
 * envio e devolução de uma Troca aparecem separados) ou uma por Solicitação (na data da
 * Movimentação original, a aberta junto com a Solicitação).
 */
export type VisaoControle = 'movimentacoes' | 'solicitacoes';

export interface LinhaControle {
  /** Única na lista (id da Movimentação ou, sem nenhuma, da Solicitação). */
  chave: string;
  solicitacao: Solicitacao;
  /** Dona da data: a própria (visão Movimentações) ou a original (visão Solicitações). */
  movimentacao: Movimentacao | null;
  /** Todas as Movimentações da Solicitação (alerta/pendência da Solicitação inteira). */
  movimentacoes: Movimentacao[];
}

export function paraMillis(timestamp: Solicitacao['createdAt'] | Movimentacao['dataPrevista'] | null | undefined): number {
  if (!timestamp) return 0;
  return typeof (timestamp as { toMillis?: () => number }).toMillis === 'function'
    ? (timestamp as { toMillis: () => number }).toMillis()
    : new Date(timestamp as unknown as string).getTime();
}

/** A que ainda não terminou (se houver) — é a que importa pra saber "o que fazer agora"
 * numa lista com várias Solicitações; se todas já terminaram, mostra a mais recente. */
export function movimentacaoRelevante(movimentacoes: Movimentacao[]): Movimentacao | null {
  if (movimentacoes.length === 0) return null;
  const ativa = movimentacoes.find((m) => m.statusMovimentacao !== 'entregue' && m.statusMovimentacao !== 'cancelada');
  return ativa ?? movimentacoes[movimentacoes.length - 1];
}

/** A aberta junto com a Solicitação (a lista vem por createdAt, da mais antiga). */
export function movimentacaoOriginal(movimentacoes: Movimentacao[]): Movimentacao | null {
  return movimentacoes[0] ?? null;
}

/**
 * Linhas da lista. Movimentações: só as que passam nos filtros de Movimentação do painel
 * (fluxo/etapa/urgência) — o mesmo número do "Na tela"; Formatação/Acessos (sem
 * Movimentação) não entram. Solicitações: uma por Solicitação.
 */
export function montarLinhas(
  solicitacoes: Solicitacao[],
  movimentacoesPorSolicitacao: Map<string, Movimentacao[]>,
  visao: VisaoControle,
  selecao: SelecaoResumo
): LinhaControle[] {
  return solicitacoes.flatMap((solicitacao) => {
    const movimentacoes = movimentacoesPorSolicitacao.get(solicitacao.id) ?? [];
    if (visao === 'solicitacoes') {
      return [{ chave: solicitacao.id, solicitacao, movimentacao: movimentacaoOriginal(movimentacoes), movimentacoes }];
    }
    return movimentacoes
      .filter((movimentacao) => movimentacaoAtende(movimentacao, selecao))
      .map((movimentacao) => ({ chave: movimentacao.id, solicitacao, movimentacao, movimentacoes }));
  });
}

/** A data que organiza a lista: a data prevista da Movimentação da linha; sem ela, a de
 * abertura da Solicitação (não some da ordenação/agrupamento). */
export function dataDaLinhaMillis(linha: LinhaControle): number {
  return linha.movimentacao?.dataPrevista ? paraMillis(linha.movimentacao.dataPrevista) : paraMillis(linha.solicitacao.createdAt);
}

/** Mais antiga primeiro; empate na mesma data desempata por nome do colaborador (A-Z). */
export function ordenarLinhas(linhas: LinhaControle[]): LinhaControle[] {
  return [...linhas].sort(
    (a, b) =>
      dataDaLinhaMillis(a) - dataDaLinhaMillis(b) ||
      (a.solicitacao.colaboradorNome ?? '').localeCompare(b.solicitacao.colaboradorNome ?? '', 'pt-BR')
  );
}

export interface GrupoLinhasPorMes {
  chave: string;
  label: string;
  itens: LinhaControle[];
}

/** Assume `linhas` já ordenadas cronologicamente — só agrupa, não reordena. */
export function agruparPorMes(linhas: LinhaControle[]): GrupoLinhasPorMes[] {
  const grupos = new Map<string, GrupoLinhasPorMes>();
  for (const linha of linhas) {
    const data = new Date(dataDaLinhaMillis(linha));
    const chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
    if (!grupos.has(chave)) {
      const labelBruto = data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      grupos.set(chave, { chave, label: labelBruto.charAt(0).toUpperCase() + labelBruto.slice(1), itens: [] });
    }
    grupos.get(chave)!.itens.push(linha);
  }
  return Array.from(grupos.values());
}

/** Mapa pra Agenda: cada Solicitação só com as Movimentações que viram item — a original
 * (visão Solicitações) ou as que passam nos filtros (visão Movimentações). */
export function movimentacoesDaAgenda(linhas: LinhaControle[]): Map<string, Movimentacao[]> {
  const mapa = new Map<string, Movimentacao[]>();
  for (const linha of linhas) {
    if (!linha.movimentacao) continue;
    mapa.set(linha.solicitacao.id, [...(mapa.get(linha.solicitacao.id) ?? []), linha.movimentacao]);
  }
  return mapa;
}

/** Busca livre — mesma regra da aba Inventário em Ativos (textoCorresponde: ignora acento,
 * espaço e pontuação). Cobre nome do colaborador, tipo, status, cliente, quem abriu, as datas
 * previstas e os equipamentos de todas as Movimentações (patrimônio, nº de série, modelo,
 * descrição livre, rastreio). */
export function correspondeTexto(
  solicitacao: Solicitacao,
  movimentacoes: Movimentacao[],
  clientes: ClientSummary[],
  colaboradores: CollaboratorPublic[],
  termo: string
): boolean {
  if (!termo.trim()) return true;
  const clienteNome = clientes.find((cliente) => cliente.id === solicitacao.clienteId)?.name ?? null;
  const solicitanteNome =
    colaboradores.find((colaborador) => colaborador.id === solicitacao.solicitanteId)?.name ?? solicitacao.solicitanteEmail;
  const equipamentos = movimentacoes.flatMap((m) => [
    m.codigoRastreio,
    paraDataFormatada(m.dataPrevista),
    ...(m.equipamentosIntake ?? []).map((item) => item.descricaoLivre),
    ...(m.equipamentosExecucao ?? []).flatMap((item) => [item.patrimonio, item.nSerie, item.modelo]),
  ]);
  return textoCorresponde(
    [
      solicitacao.colaboradorNome,
      TIPO_SOLICITACAO_LABELS[solicitacao.tipoSolicitacao],
      STATUS_SOLICITACAO_LABELS[solicitacao.status],
      clienteNome,
      solicitanteNome,
      ...equipamentos,
    ],
    termo
  );
}

/** Totais do "Listar por": tudo da aba, sem filtro nenhum, menos Solicitações canceladas/
 * excluídas e Movimentações canceladas. */
export function totaisAtivos(
  solicitacoes: Solicitacao[],
  movimentacoesPorSolicitacao: Map<string, Movimentacao[]>
): Record<VisaoControle, number> {
  const ativas = solicitacoes.filter((s) => s.status !== 'cancelada' && s.status !== 'excluida');
  return {
    solicitacoes: ativas.length,
    movimentacoes: ativas.reduce(
      (soma, s) => soma + (movimentacoesPorSolicitacao.get(s.id) ?? []).filter((m) => m.statusMovimentacao !== 'cancelada').length,
      0
    ),
  };
}

/** Solicitações na ordem da visão Solicitações (data da Movimentação original) — usada
 * pelo Kanban e pela Agenda, que recebem a lista de Solicitações. */
export function ordenarSolicitacoes(lista: Solicitacao[], movimentacoesPorSolicitacao: Map<string, Movimentacao[]>): Solicitacao[] {
  return ordenarLinhas(
    lista.map((solicitacao) => {
      const movimentacoes = movimentacoesPorSolicitacao.get(solicitacao.id) ?? [];
      return { chave: solicitacao.id, solicitacao, movimentacao: movimentacaoOriginal(movimentacoes), movimentacoes };
    })
  ).map((linha) => linha.solicitacao);
}
